import _ from 'lodash/fp'
import gql from 'graphql-tag'
import { all, call, put, take, takeLatest } from 'redux-saga/effects'

import { actions, types } from '../reducers/clubStatus'

import { getCurrentClubId } from './network'
import { apolloClient } from '@fe/business/src/apollo'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const CLUB_ANNOUNCEMENT_QUERY = gql`
  query ClubAnnouncement($clubId: ID!) {
    ClubQuery {
      clubAnnouncement(clubId: $clubId) {
        header
        body
        level
        start
        end
        images
        userId
      }
    }
  }
`

export function * loadClubStatusEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.query], {
      query: CLUB_ANNOUNCEMENT_QUERY,
      variables: { clubId },
      fetchPolicy: 'network-only'
    })
    // resolver returns null when the club has no announcement yet
    return _.get('data.ClubQuery.clubAnnouncement', response) || {}
  } catch (error) {
    return { error }
  }
}

export function * saveClubStatusEffect (payload) {
  try {
    const clubId = yield call(getCurrentClubId)
    const response = yield call([apolloClient, apolloClient.mutate], {
      mutation: gql`
        mutation SetClubAnnouncement($input: SetClubAnnouncementInput!) {
          ClubMutation {
            setClubAnnouncement(input: $input) {
              header
              body
              level
              start
              end
              images
              userId
            }
          }
        }
      `,
      variables: {
        input: {
          clubId,
          header: _.get('header', payload),
          body: _.get('body', payload),
          level: _.get('level', payload),
          start: _.get('start', payload),
          end: _.get('end', payload),
          images: _.get('images', payload)
        }
      }
    })

    return { data: _.get('data.ClubMutation.setClubAnnouncement', response) }
  } catch (error) {
    return { error }
  }
}

export function * deleteClubStatusEffect () {
  try {
    const clubId = yield call(getCurrentClubId)
    yield call([apolloClient, apolloClient.mutate], {
      mutation: gql`
        mutation DeleteClubAnnouncement($clubId: ID!) {
          ClubMutation {
            deleteClubAnnouncement(clubId: $clubId)
          }
        }
      `,
      variables: { clubId }
    })

    return {}
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

export function * loadClubStatusFlow () {
  const clubStatus = yield call(loadClubStatusEffect)
  if (clubStatus.error) {
    yield put(actions.loadError(clubStatus.error))
  } else {
    yield put(actions.loadSuccess(clubStatus))
  }
  yield put(actions.loadRequestDone())
}

export function * saveClubStatusFlow ({ payload }) {
  const result = yield call(saveClubStatusEffect, payload)

  if (!result.error) {
    yield put(actions.loadRequest())
    yield take(types.LOAD_REQUEST_DONE)
  } else {
    yield put(actions.saveError(result.error))
  }

  yield put(actions.saveRequestDone())
}

export function * deleteClubStatusFlow () {
  const result = yield call(deleteClubStatusEffect)

  if (!result.error) {
    yield put(actions.loadRequest())
    yield take(types.LOAD_REQUEST_DONE)
  } else {
    yield put(actions.deleteError(result.error))
  }

  yield put(actions.deleteRequestDone())
}

export default function * () {
  return yield all([
    takeLatest(types.DELETE_REQUEST, deleteClubStatusFlow),
    takeLatest(types.SAVE_REQUEST, saveClubStatusFlow),
    takeLatest(types.LOAD_REQUEST, loadClubStatusFlow)
  ])
}
